// NATS browser-creds manager.
//
// Holds the active session's {jwt, seed} in a module-level closure for
// the lifetime of the page. NEVER passed through:
//   - Zustand store (would surface in any devtools that reads stores)
//   - TanStack Query cache (visible via queryClient.getQueryData / devtools)
//   - React state / context (visible in React DevTools)
//   - localStorage / sessionStorage / cookie / URL
//
// One in-flight Promise across concurrent callers so a render-loop that
// hits getCreds() from twenty places only fires one mint request.
//
// Re-mints when the JWT is within 5 minutes of expiry. The auth-store
// subscription in jsClient.ts calls clearCreds() on logout so creds drop
// out of memory before the user leaves the tab.

import { useAuthStore } from '@/store/authStore';
import { API_BASE } from '@/apiConfig';

interface BrowserCreds {
  natsURL:     string;
  jwt:         string;
  seed:        string;     // SU…  raw nkey seed string; converted to bytes at use
  expiresAt:   number;     // unix ms
  companySlug: string;
}

interface MintResponse {
  nats_url:     string;
  jwt:          string;
  seed:         string;
  expires_at:   number;    // unix seconds
  company_slug: string;
}

const REMINT_LEAD_MS = 5 * 60_000;

// Module-level state. NOT exported — only `getCreds` / `clearCreds`
// callable from outside this file.
let creds:    BrowserCreds | null = null;
let inflight: Promise<BrowserCreds> | null = null;

/** Return the current creds, minting (or re-minting) on demand. */
export async function getCreds(): Promise<BrowserCreds> {
  if (creds && creds.expiresAt > Date.now() + REMINT_LEAD_MS) {
    return creds;
  }
  if (inflight) return inflight;
  inflight = mint().finally(() => { inflight = null; });
  return inflight;
}

/** Drop the cached creds. Call on logout / company switch. */
export function clearCreds(): void {
  creds = null;
  inflight = null;
}

async function mint(): Promise<BrowserCreds> {
  const token = useAuthStore.getState().user?.token;
  if (!token) throw new Error('cannot mint nats creds without zitadel session');
  const company = useAuthStore.getState().company;
  const url = `${API_BASE}/nats-creds/browser-creds${company ? `?company=${encodeURIComponent(company)}` : ''}`;
  const res = await fetch(url, {
    method:  'POST',
    headers: { Authorization: `Bearer ${token}` },
    credentials: 'omit', // no cookies; bearer is sufficient
  });
  if (!res.ok) {
    throw new Error(`browser-creds mint failed: ${res.status}`);
  }
  const body = await res.json() as MintResponse;
  creds = {
    natsURL:     body.nats_url,
    jwt:         body.jwt,
    seed:        body.seed,
    expiresAt:   body.expires_at * 1000,
    companySlug: body.company_slug,
  };
  return creds;
}
